import type { DashWriteTemplate } from "@mirage/analytics/events/enums/dash_write_template";
import { useCombinedOnboardingInfo } from "@mirage/growth/onboarding/hooks/useCombinedOnboardingInfo";
import { SUGGESTION_CHIPS_STATE_MAP } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/chip_state_map";
import { FILE_CATEGORIES_INVERTED_INDEX } from "@mirage/mosaics/Chat/components/chat/instructions/bridge_growth/constants";
import type { QuerySuggestionResult } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/types";
import type { ConversationSource } from "@mirage/service-fss-bridge/service/bridge_types";
import { ProfilingQuestionKindOfWorkAnswer } from "@mirage/service-onboarding-surveys/service/onboarding-surveys/self-serve-profiling";
import type {
  NestedSurfaceEntry,
  PAPPromptEnum,
  Suggestion,
  SurfaceEntry,
} from "../types";
import { SuggestionType, UserJourney } from "../types";

// Helper to get dashWriteTemplate by role (avoids circular dependency)
const getDashWriteEnumByRole = (
  role: ProfilingQuestionKindOfWorkAnswer | null,
): DashWriteTemplate => {
  switch (role) {
    case ProfilingQuestionKindOfWorkAnswer.Creative:
    case ProfilingQuestionKindOfWorkAnswer.Marketing:
      return "marketing_blog_post";
    case ProfilingQuestionKindOfWorkAnswer.Sales:
      return "sales_outreach_email";
    default:
      return "status_report";
  }
};

// returns an array of suggestion objects that describe the suggestions to render
export function useEmptyStateSuggestions({
  source,
  isFirstTimeChat,
  filesCount,
  extension,
}: {
  source?: ConversationSource;
  isFirstTimeChat: boolean;
  filesCount?: number;
  extension?: string;
}): { suggestions: Suggestion[]; loading: boolean } {
  const { role, loading } = useCombinedOnboardingInfo();
  const dashWriteTemplate = getDashWriteEnumByRole(role);

  const userJourney = isFirstTimeChat ? UserJourney.FTUX : UserJourney.ATUX;
  const surface = parseSurface(source, filesCount || 0);
  const extensionType = getExtensionType(extension);

  const surfaceEntry = SUGGESTION_CHIPS_STATE_MAP[userJourney]?.[surface];
  const rawResult = isNestedSurfaceEntry(surfaceEntry)
    ? surfaceEntry?.[extensionType]
    : surfaceEntry;

  // Process suggestions to replace default enum values with actual role-based values
  const result = (rawResult || []).map((suggestion) => {
    if (
      suggestion.type === SuggestionType.FSS_WRITE &&
      suggestion.enum === "status_report" &&
      dashWriteTemplate !== "status_report"
    ) {
      return { ...suggestion, enum: dashWriteTemplate };
    }
    return suggestion;
  });

  return { suggestions: result, loading };
}

// Parse the surface type from the source string and file count, since the source doesn't always map cleanly to the surface
const parseSurface = (
  source: ConversationSource | undefined,
  filesCount: number,
) => {
  let result = source || "fss-unscoped";
  if (source === "fss-file-preview" || source === "fss-file") {
    result = filesCount > 1 ? "fss-files" : source;
  }

  return result;
};

// This function is used to determine the type of file based on its extension
// for files with multiple types, it returns the first type
const getExtensionType = (extension?: string) => {
  if (!extension) return "other";
  const extensionLower = extension.toLowerCase();
  return FILE_CATEGORIES_INVERTED_INDEX[extensionLower]?.[0] || "other";
};

const isNestedSurfaceEntry = (
  entry: SurfaceEntry,
): entry is NestedSurfaceEntry => {
  return typeof entry === "object" && !Array.isArray(entry);
};

/**
 * Helper function to extract prompt enums from suggestions with actual content.
 * This function is extracted to avoid recreation on every render.
 *
 * @param suggestions - Array of suggestion objects from useEmptyStateSuggestions
 * @param querySuggestions - Array of query suggestion results from useQuerySuggestions
 * @returns Array of PAPPromptEnum values for suggestions that have actual content
 */
export const getPromptEnums = (
  suggestions: Suggestion[],
  querySuggestions: QuerySuggestionResult[],
): PAPPromptEnum[] => {
  return suggestions
    .slice(0, 3)
    .map((suggestion, index) => {
      // For dynamic suggestions, check if there's actual text content
      if (suggestion.type === SuggestionType.DYNAMIC) {
        const dynamicSuggestion = querySuggestions[index];
        if (!dynamicSuggestion?.text) {
          return null; // No actual content, don't log this enum
        }
      }
      // Has content (static/contextual always do, dynamic checked above)
      // For FSS_WRITE suggestions, convert DashWriteTemplate to PAPPromptEnum
      if (suggestion.type === SuggestionType.FSS_WRITE) {
        return suggestion.enum as PAPPromptEnum;
      }
      return suggestion.enum as PAPPromptEnum;
    })
    .filter((enum_value): enum_value is PAPPromptEnum => enum_value != null); // Type-safe filter
};
